<template>
  <div>
    <simpletoolbar></simpletoolbar>
    <div class="max-w-4xl mx-auto px-4 py-8">
      <h1 class="text-3xl font-bold mb-8 text-stone-800 dark:text-stone-100">Privacy Policy</h1>

      <!-- Introduction Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Introduction</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Spin the Names is developed and maintained by an independent developer. This privacy policy explains how Spin
          the Names collects, uses, and safeguards your information when you use the app.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Please read this privacy policy carefully. By using Spin the Names, you agree to the collection and use of
          information
          in accordance with this policy.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          It's important to note that Spin the Names doesn't collect any of the data you enter for spins. The data you
          provide
          is used solely for the app's functional purposes.
        </p>
      </section>

      <!-- Information Collection -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Information Collection</h2>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Personal Information</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            When you create an account, we collect:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Email address</li>
            <li>Username (if provided)</li>
            <li>Profile information (if provided)</li>
            <li>Authentication information from third-party services (like Google) if you use them to sign in</li>
          </ul>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Wheel Content</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Spin the Names stores the content you add to your wheels, including:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Names, options, or text entries you add to wheels</li>
            <li>Wheel titles and descriptions</li>
            <li>Wheel customization settings</li>
          </ul>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            This information is necessary to provide the core service, allowing you to save and access your wheels
            across devices.
            The app doesn't use the names or options you enter for any purpose other than providing functionality.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Usage Data</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Spin the Names automatically collects certain information when you visit, use or navigate the website,
            including:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Device information (browser type, operating system, device type)</li>
            <li>IP address</li>
            <li>Pages visited and features used</li>
            <li>Time and date of your visit</li>
            <li>Time spent on pages</li>
            <li>Referring website or source</li>
          </ul>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            This information helps understand how users interact with the application, identify and fix issues,
            and improve the service.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Cookies and Similar Technologies</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Spin the Names uses cookies and similar tracking technologies to track activity on the website and store
            certain information.
            Cookies are files with a small amount of data that may include an anonymous unique identifier.
          </p>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            These technologies help:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Keep you logged in</li>
            <li>Remember your preferences</li>
            <li>Understand how you use our application</li>
            <li>Improve our service based on your usage patterns</li>
          </ul>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            However, if you do not accept cookies, you may not be able to use some portions of the service.
          </p>
        </div>
      </section>

      <!-- How Your Information Is Used -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">How Your Information Is Used</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          The information collected is used for various purposes, including:
        </p>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Providing and Improving Our Service
          </h3>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Operating and maintaining your account</li>
            <li>Storing and retrieving your wheel configurations</li>
            <li>Personalizing your experience</li>
            <li>Developing new features and functionality</li>
            <li>Fixing bugs and addressing technical issues</li>
          </ul>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Communication</h3>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Responding to your inquiries and support requests</li>
            <li>Sending service-related announcements and updates</li>
            <li>Informing you about changes to our terms or privacy policy</li>
          </ul>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Your information is not used for marketing communications unless you explicitly opt in to receive such
            communications.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Analytics and Service Improvement</h3>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Understanding how users interact with our application</li>
            <li>Measuring the effectiveness of features</li>
            <li>Identifying areas for improvement</li>
            <li>Testing new functionality</li>
          </ul>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            This information is used in aggregate form and helps make data-driven decisions about how to enhance the
            service.
          </p>
        </div>
      </section>

      <!-- Data Sharing and Disclosure Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Data Sharing and Disclosure</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          Spin the Names takes your privacy seriously and does not sell your personal information. However, your
          information may be shared in the following circumstances:
        </p>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Service Providers</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Spin the Names may employ third-party companies and individuals to facilitate the Service, provide the
            Service,
            perform Service-related services, or assist in analyzing how the Service is used. These third parties have
            access to your Personal Information only to perform these tasks and are obligated not to disclose
            or use it for any other purpose.
          </p>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Examples include:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Cloud hosting providers</li>
            <li>Analytics services</li>
            <li>Customer support tools</li>
            <li>Authentication services</li>
          </ul>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Advertising Partners</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            We work with third-party advertising partners, including Google AdSense, to serve advertisements on our
            Service.
            These partners may use cookies and similar technologies to collect usage data and serve ads based on your
            visit to this Service and other sites on the Internet.
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Third party vendors, including Google, use cookies to serve ads based on a user's prior visits to your
              website or other websites.</li>
            <li>Google's use of advertising cookies enables it and its partners to serve ads to your users based on
              their visit to your sites and/or other sites on the Internet.</li>
            <li>Users may opt out of personalized advertising by visiting <a href="https://www.google.com/settings/ads"
                target="_blank" rel="noopener noreferrer" class="text-primary-600 hover:underline">Google Ads
                Settings</a>.</li>
          </ul>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">When You Choose to Share</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            If you use our sharing features to publicly share a wheel, the wheel's content and configuration
            will be accessible to anyone with the link. You should not include sensitive personal information
            in shared wheels.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Legal Requirements</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Your information may be disclosed if required by law or in response to valid requests by public authorities
            (e.g., a court or a government agency). Information may also be disclosed to:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>Comply with a legal obligation</li>
            <li>Protect and defend the rights or property of Spin the Names</li>
            <li>Prevent or investigate possible wrongdoing in connection with the Service</li>
            <li>Protect the personal safety of users of the Service or the public</li>
            <li>Protect against legal liability</li>
          </ul>
        </div>
      </section>

      <!-- Data Security Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Data Security</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          The security of your data is important. Spin the Names uses commercially acceptable means to protect your
          Personal Information, but no method of transmission over the Internet or method of electronic storage is 100%
          secure.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          The app implements appropriate technical and organizational measures to protect your information, including:
        </p>
        <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
          <li>Encryption of sensitive data</li>
          <li>Secure authentication mechanisms</li>
          <li>Regular security assessments</li>
          <li>Limited access to personal information by our staff</li>
          <li>Continuous monitoring for potential vulnerabilities</li>
        </ul>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          While every effort is made to use commercially acceptable means to protect your Personal Information,
          absolute security cannot be guaranteed. If you have reason to believe that your interaction with the app
          is no longer secure, please immediately contact us at ash@spinthenames.com.
        </p>
      </section>

      <!-- Your Rights and Choices Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Your Rights and Choices</h2>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Account Information</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            You can review, update, or delete your account information at any time by logging into your account
            settings.
            If you delete your account, we will delete your profile information and saved wheels, but we may retain
            certain
            information for analytical purposes and record keeping integrity, as well as to prevent fraud, resolve
            disputes,
            enforce our terms or other policies, or take other actions as required or permitted by law.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Your Data Protection Rights</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Depending on your location, you may have the following data protection rights:
          </p>
          <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
            <li>The right to access, update, or delete your personal information</li>
            <li>The right to correct or update your personal information if it is inaccurate</li>
            <li>The right to object to our processing of your personal information</li>
            <li>The right to request that we restrict the processing of your personal information</li>
            <li>The right to data portability (receiving a copy of your personal information in a structured,
              commonly used, and machine-readable format)</li>
            <li>The right to withdraw consent for processing your personal information</li>
          </ul>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            To exercise these rights, please contact us through the contact information provided at the end of this
            policy.
          </p>
        </div>

        <div class="mb-6">
          <h3 class="text-xl font-medium mb-2 text-stone-800 dark:text-stone-100">Cookies and Tracking Technologies</h3>
          <p class="mb-2 text-stone-700 dark:text-stone-300">
            Most web browsers are set to accept cookies by default. If you prefer, you can usually choose to set your
            browser
            to remove or reject cookies. Please note that if you choose to remove or reject cookies, this could affect
            the
            availability and functionality of our Service.
          </p>
        </div>
      </section>

      <!-- Children's Privacy Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Children's Privacy</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          This Service is not directed to anyone under the age of 13. Spin the Names does not knowingly collect
          personally identifiable
          information from children under 13. If you are a parent or guardian and you are aware that your child has
          provided
          Personal Information, please contact us at ash@spinthenames.com. If it is discovered that Personal Information
          has been collected from children under 13 without verification of parental consent, steps will be taken to
          remove that information.
        </p>
      </section>

      <!-- Changes to This Privacy Policy Section -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Changes to This Privacy Policy</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          This Privacy Policy may be updated from time to time. You will be notified of any changes by the posting of
          the new Privacy Policy
          on this page and updating the "effective date" at the top of this Privacy Policy.
        </p>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are
          effective
          when they are posted on this page.
        </p>
      </section>

      <!-- Contact Information -->
      <section class="mb-10">
        <h2 class="text-2xl font-semibold mb-4 text-stone-800 dark:text-stone-100">Contact Information</h2>
        <p class="mb-4 text-stone-700 dark:text-stone-300">
          If you have any questions about this Privacy Policy, please contact:
        </p>
        <ul class="list-disc pl-8 mb-4 text-stone-700 dark:text-stone-300">
          <li>By email: ash@spinthenames.com</li>
        </ul>
      </section>

      <!-- Back to Top link -->
      <div class="text-center mb-8">
        <a href="#" @click.prevent="scrollToTop"
          class="inline-flex items-center px-4 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300">
          <i class="fas fa-arrow-up mr-2"></i> Back to Top
        </a>
      </div>

      <div
        class="text-center text-sm text-stone-500 dark:text-stone-400 mt-12 pt-6 border-t border-stone-200 dark:border-stone-700">
        <p>Effective Date: June 5, 2025</p>
      </div>
    </div>
    <app-footer />
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import simpletoolbar from '@components/ui/simpletoolbar.vue';
import appFooter from '@components/ui/appFooter.vue';
import { useSeo } from '@/composables/useSeo';

// Set up SEO for the privacy policy page
useSeo({
  title: 'Privacy Policy - Spin the Names',
  description: 'Learn how Spin the Names collects, uses, and protects your personal information. Our privacy policy explains data practices and your rights.',
  ogType: 'article',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': 'Privacy Policy - Spin the Names',
    'description': 'Learn how Spin the Names collects, uses, and protects your personal information.',
    'datePublished': '2025-06-05',
    'dateModified': '2025-06-05',
    'publisher': {
      '@type': 'Organization',
      'name': 'Spin the Names',
      'url': 'https://spinthenames.com'
    }
  }
});

// Scroll to top when page loads
onMounted(() => {
  window.scrollTo(0, 0);
});

// Method to scroll to top when "Back to Top" is clicked
function scrollToTop() {
  window.scrollTo({
    top: 0,
    behavior: 'smooth'
  });
}
</script>
